import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createGame, createEmptyBoard, canPlaceShip, placeShip, attack, allSunk, autoPlaceShips, cpuAttack, notifyCpuHit } from "./engine.js";
import { SHIPS, GRID_SIZE } from "./types.js";
import type { BattleshipGame, Direction, Position, PlayerBoard } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type Mode = "cpu" | "local" | "multiplayer";
type MpView = "lobby" | "join-form";

export default function BattleshipPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [mode, setMode] = useState<Mode>("cpu");
  const [game, setGame] = useState<BattleshipGame>(() => createGame(false, true));
  const [message, setMessage] = useState("");
  const [dir, setDir] = useState<Direction>("h");
  const [sunkMsg, setSunkMsg] = useState("");
  const [placePreview, setPlacePreview] = useState<Position[]>([]);

  const [mpView, setMpView] = useState<MpView>("lobby");
  const [mpConnected, setMpConnected] = useState(false);
  const [mpPlayerId, setMpPlayerId] = useState<0 | 1>(0);
  const [roomCode, setRoomCode] = useState("");
  const [mpToken, setMpToken] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [yourTurn, setYourTurn] = useState(false);
  const [mpError, setMpError] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [p1Name, setP1Name] = useState("Player 1");
  const [p2Name, setP2Name] = useState("Player 2");

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);
  const roomCodeRef = useRef(roomCode);
  const mpPlayerIdRef = useRef(mpPlayerId);
  roomCodeRef.current = roomCode;
  mpPlayerIdRef.current = mpPlayerId;

  const cpuTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLocalPass = mode === "local" && game.phase === "playing";
  const [localHide, setLocalHide] = useState(false);

  const myIdx = mode === "multiplayer" ? mpPlayerId : game.turn;
  const oppIdx = myIdx === 0 ? 1 : 0;

  function startNewGame() {
    const isCPU2 = mode === "cpu";
    const g = createGame(false, isCPU2);
    g.players[0].name = p1Name.trim() || "Player 1";
    g.players[1].name = mode === "cpu" ? "Computer" : p2Name.trim() || "Player 2";
    if (g.players[1].isCPU) g.players[1].board = autoPlaceShips(g.players[1].board);
    setGame(g);
    setMessage(`Place your ships, ${g.players[0].name}.`);
    setSunkMsg("");
    setPlacePreview([]);
  }

  function handlePlaceCell(r: number, c: number) {
    if (game.phase !== "placement") return;
    const player = game.players[game.turn];
    if (player.board.ships.length >= SHIPS.length) return;
    const ship = SHIPS[player.board.ships.length];
    const pos: Position = { row: r, col: c };
    if (!canPlaceShip(player.board, ship.size, pos, dir)) return;
    const newBoard = placeShip(player.board, ship, pos, dir);
    const players = game.players.map((p) => (p === player ? { ...p, board: newBoard } : { ...p })) as [typeof game.players[0], typeof game.players[1]];
    const done = newBoard.ships.length >= SHIPS.length;
    if (done) {
      const nextTurn = game.turn === 0 ? 1 : 0;
      const otherDone = players[nextTurn].board.ships.length >= SHIPS.length;
      const phase = otherDone ? "playing" : "placement";
      setGame({ ...game, players, turn: nextTurn, phase: phase as any, placementShip: 0 });
      if (otherDone) setMessage("All ships placed! Attack!");
      else if (mode === "local") { setLocalHide(true); setMessage("Pass device to opponent to place ships."); }
      else setMessage(`${players[nextTurn].name}, place your ships.`);
    } else {
      setGame({ ...game, players, placementShip: newBoard.ships.length });
    }
    setPlacePreview([]);
  }

  function handleAttack(r: number, c: number) {
    if (game.phase !== "playing") return;
    const opponent = game.players[oppIdx];
    const pos: Position = { row: r, col: c };
    if (opponent.board.attackGrid[r][c] !== "unknown") return;
    const result = attack(opponent.board, pos);
    const players = [...game.players] as [typeof game.players[0], typeof game.players[1]];
    players[oppIdx] = { ...opponent, board: result.board };
    let msg = result.result === "hit" ? "Hit!" : "Miss.";
    if (result.sunk) { msg += ` ${result.sunk.name} sunk!`; setSunkMsg(`${result.sunk.name} sunk!`); }
    if (allSunk(result.board)) {
      setGame({ ...game, players, phase: "finished", winner: game.turn });
      setMessage(`${game.players[game.turn].name} wins!`);
      return;
    }
    const nextTurn = game.turn === 0 ? 1 : 0;
    if (mode === "local" && !game.players[nextTurn].isCPU) setLocalHide(true);
    setGame({ ...game, players, turn: nextTurn });
    setMessage(`${players[nextTurn].name}'s turn`);
    setTimeout(() => setSunkMsg(""), 2000);
  }

  useEffect(() => {
    if (!game || game.winner !== null) return;
    const player = game.players[game.turn];
    if (!player.isCPU) return;
    if (game.phase === "playing") {
      cpuTimer.current = setTimeout(() => {
        const opp = game.players[game.turn === 0 ? 1 : 0];
        const pos = cpuAttack(opp.board, game.turn);
        handleAttack(pos.row, pos.col);
      }, 600);
    }
    return () => { if (cpuTimer.current) clearTimeout(cpuTimer.current); };
  }, [game?.turn, game?.phase, game?.players]);

  async function apiPost(path: string, body: object) { try { const r = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }); return await r.json(); } catch { return { type: "error", message: "Network error." }; } }
  function handleServerEvent(event: any) {
    switch (event.type) {
      case "opponent-joined": setOpponentName(event.playerName); setMpConnected(true); setMessage(`${event.playerName} joined!`); break;
      case "game-state": setGame(event.game); setYourTurn(event.yourTurn); break;
      case "opponent-left": setMpConnected(false); setMpView("lobby"); setMessage("Opponent left."); break;
    }
  }
  const startPolling = useCallback(() => { if (pollingRef.current) return; pollingRef.current = true; async function poll() { if (!pollingRef.current) return; const c = roomCodeRef.current, p = mpPlayerIdRef.current; if (!c) { pollingRef.current = false; return; } try { const r = await fetch(`/api/poll?code=${encodeURIComponent(c)}&playerId=${p + 1}`); const d = await r.json(); for (const e of d.events) { handleServerEvent(e); if (e.type === "opponent-left") { pollingRef.current = false; return; } } } catch {} if (pollingRef.current) pollRef.current = setTimeout(poll, 200); } poll(); }, []);
  function stopPolling() { pollingRef.current = false; if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } }
  useEffect(() => { return () => stopPolling(); }, []);
  async function handleCreateRoom() { const r = await apiPost("/api/create-room", { playerName: p1Name, playerColor: "blue", gameType: "battleship" }); if (r.type === "error") { setMpError(r.message); return; } roomCodeRef.current = r.code; mpPlayerIdRef.current = (r.playerId - 1) as 0 | 1; setRoomCode(r.code); setMpPlayerId(0); setMpToken(r.token); setMessage(`Room: ${r.code}`); startPolling(); }
  async function handleJoinRoom() { if (!joinCode.trim()) { setMpError("Enter a code."); return; } const r = await apiPost("/api/join-room", { code: joinCode.trim(), playerName: p2Name, playerColor: "red" }); if (r.type === "error") { setMpError(r.message); return; } roomCodeRef.current = joinCode.trim(); mpPlayerIdRef.current = 1; setMpPlayerId(1); setMpToken(r.token); setRoomCode(joinCode.trim()); setOpponentName(r.opponentName); setMpConnected(true); setMessage(`Connected with ${r.opponentName}.`); startPolling(); }
  async function handleLeaveGame() { await apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId + 1, token: mpToken }); stopPolling(); setMpConnected(false); setMpView("lobby"); setMessage("Left."); }

  function renderCellLabel(r: number, c: number) { if (r === 0 && c === 0) return ""; if (r === 0) return String.fromCharCode(64 + c); if (c === 0) return String(r); return ""; }
  function getMyAttackClass(r: number, c: number): string { const opp = game.players[oppIdx]; const cell = opp.board.attackGrid[r][c]; let cls = "bs-cell"; if (cell === "unknown") cls += " unknown"; else if (cell === "hit") cls += " hit"; else cls += " miss"; return cls; }
  function getPlacementClass(r: number, c: number): string { const player = game.players[game.turn]; let cls = "bs-cell"; const inPreview = placePreview.some((p) => p.row === r && p.col === c); if (inPreview) cls += " placing"; else if (player.board.grid[r][c] === "ship") cls += " ship"; else cls += " water"; return cls; }
  function getOwnBoardClass(r: number, c: number): string { const player = game.players[myIdx]; let cls = "bs-cell"; if (player.board.grid[r][c] === "ship") cls += " ship"; else cls += " water"; return cls; }
  function handlePlaceHover(r: number, c: number) {
    if (game.phase !== "placement") return; const player = game.players[game.turn];
    if (player.board.ships.length >= SHIPS.length) return;
    const ship = SHIPS[player.board.ships.length]; const preview: Position[] = []; let ok = true;
    for (let i = 0; i < ship.size; i++) { const rr = dir === "v" ? r + i : r; const cc = dir === "h" ? c + i : c; if (rr >= GRID_SIZE || cc >= GRID_SIZE || player.board.grid[rr][cc] !== "empty") { ok = false; break; } preview.push({ row: rr, col: cc }); }
    setPlacePreview(ok ? preview : []);
  }

  return (
    <div className="game-shell">
      <ConfettiSplash
        active={game.phase === "finished"}
        winnerName={game.phase === "finished" ? game.players[game.winner!].name : undefined}
      />
      {!hideTitle && <h1 className="title">Battleship Wars</h1>}
      <section className="control-panel">
        <div className="row two-col">
          <label>Mode <select value={mode} onChange={(e) => { const m = e.target.value as Mode; setMode(m); if (m !== "multiplayer") { setGame(createGame(false, m === "cpu")); if (m === "cpu") { const g = createGame(false, true); g.players[1].board = autoPlaceShips(g.players[1].board); setGame(g); } else setGame(createGame(false, false)); } }}>
            <option value="cpu">Single Player vs Computer</option>
            <option value="local">Two Players Local</option>
            <option value="multiplayer">Multiplayer Online</option>
          </select></label>
          {game.phase === "placement" && game.turn === 0 && game.players[0].board.ships.length < SHIPS.length && (
            <div className="bs-dir-toggle"><button onClick={() => setDir((d) => d === "h" ? "v" : "h")}>{dir === "h" ? "⇔ Horizontal" : "⇕ Vertical"}</button></div>
          )}
        </div>
        {mode !== "multiplayer" && (
          <div className="row two-col">
            <label>Player 1 <input value={p1Name} onChange={(e) => setP1Name(e.target.value)} maxLength={16} /></label>
            <label>{mode === "cpu" ? "Computer" : "Player 2"} <input value={mode === "cpu" ? "Computer" : p2Name} onChange={(e) => setP2Name(e.target.value)} maxLength={16} disabled={mode === "cpu"} /></label>
          </div>
        )}
        {mode === "multiplayer" && !mpConnected && mpView === "lobby" && (
          <div className="mp-lobby"><h3 className="mp-heading">Multiplayer</h3><div className="mp-actions"><button className="mp-btn" onClick={handleCreateRoom}>Create Room</button><button className="mp-btn mp-btn-secondary" onClick={() => setMpView("join-form")}>Join Room</button></div></div>
        )}
        {mode === "multiplayer" && !mpConnected && mpView === "join-form" && (
          <div className="mp-lobby"><h3 className="mp-heading">Join Room</h3><div className="mp-form"><label>Code <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} /></label><div className="mp-actions"><button className="mp-btn" onClick={handleJoinRoom}>Join</button><button className="mp-btn mp-btn-secondary" onClick={() => setMpView("lobby")}>Back</button></div></div></div>
        )}
        {mode === "multiplayer" && roomCode && !mpConnected && <p className="subtle">Code: <strong>{roomCode}</strong> — waiting...</p>}
        {mode === "multiplayer" && mpConnected && <p className="subtle">Connected with <strong>{opponentName}</strong></p>}
        <p className="status">{message}</p>
        {sunkMsg && <p className="subtle" style={{ color: "#f88" }}>{sunkMsg}</p>}
        {game.phase !== "placement" && mode !== "multiplayer" && <div className="actions"><button onClick={startNewGame}>New Game</button></div>}
        {mode === "multiplayer" && mpConnected && <div className="actions"><button onClick={handleLeaveGame}>Leave</button></div>}
        <div className="bs-ships-list">
          {SHIPS.map((s, i) => { const player = game.players[myIdx]; const ship = player.board.ships[i]; let cls = "bs-ship-badge"; if (!ship) cls += game.phase === "placement" && i === player.board.ships.length ? " current" : " pending"; else if (ship.hits.every((h) => h)) cls += " sunk"; else cls += " done"; return <span key={s.name} className={cls}>{s.name} ({s.size})</span>; })}
        </div>
      </section>

      {game.phase !== "finished" && (
        <section className="board-panel">
          {isLocalPass && !game.players[game.turn].isCPU ? (
            <div style={{ textAlign: "center", padding: 40 }}><p>{game.players[game.turn].name}'s turn. Pass the device!</p><button onClick={() => setLocalHide(false)}>Ready</button></div>
          ) : (
            <div className="bs-grids">
              <div className="bs-grid-wrap">
                <h3>Your Fleet</h3>
                <div className="bs-grid">
                  {Array.from({ length: GRID_SIZE }, (_, r) => Array.from({ length: GRID_SIZE }, (_, c) => (<div key={`own-${r}-${c}`} className={getOwnBoardClass(r, c)}>{renderCellLabel(r, c)}</div>)))}
                </div>
              </div>
              <div className="bs-grid-wrap">
                <h3>{game.phase === "placement" ? `Place Ships (${game.players[game.turn].name})` : `Target Enemy (${game.players[oppIdx].name})`}</h3>
                <div className="bs-grid">
                  {Array.from({ length: GRID_SIZE }, (_, r) => Array.from({ length: GRID_SIZE }, (_, c) => (
                    game.phase === "placement" ? (
                      <button key={`pl-${r}-${c}`} className={getPlacementClass(r, c)} onMouseEnter={() => handlePlaceHover(r, c)} onMouseLeave={() => setPlacePreview([])} onClick={() => handlePlaceCell(r, c)} disabled={game.players[game.turn].board.ships.length >= SHIPS.length} />
                    ) : (
                      <button key={`atk-${r}-${c}`} className={getMyAttackClass(r, c)} onClick={() => handleAttack(r, c)} disabled={game.players[myIdx].isCPU || (mode === "multiplayer" && !yourTurn) || (mode === "local" && !game.players[game.turn].isCPU && game.turn !== myIdx)} />
                    )
                  )))}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {game.phase !== "finished" && (
        <div className="how-to-play">
          <p className="how-title">How to Play</p>
          <p>1. Place your 5 ships on the grid — tap a cell to place, toggle direction with the button.</p>
          <p>2. Once both fleets are placed, take turns attacking the enemy grid.</p>
          <p>3. Click a cell on the enemy grid to fire — a hit marks red ✕, a miss shows blue ·.</p>
          <p>4. Sink all 5 enemy ships (Carrier, Battleship, Cruiser, Submarine, Destroyer) to win!</p>
        </div>
      )}

      {game.phase === "finished" && (
        <section className="board-panel" style={{ textAlign: "center" }}>
          <h2 style={{ color: "#ffd44d" }}>{game.players[game.winner!].name} wins!</h2>
          <button onClick={startNewGame}>Play Again</button>
        </section>
      )}
    </div>
  );
}